/**
 * 睡眠カード（ヘルスダッシュボード用）
 *
 * その日に起きた睡眠すべて（前日から寝た分・昼寝を含む）の合計と、睡眠ごとのステージをタイムライン表示。
 * グラフ部分は共有 SleepGraph コンポーネントを使用。
 */

import { ChevronRight, Moon } from 'lucide-react'
import { useMemo } from 'react'
import { Link } from 'react-router'

import type { GarminSleepSummary } from '@/api/types/garmin'
import { formatSecondsJa } from '@/lib/time-format'
import { cn } from '@/lib/utils'
import { PATHS } from '@/routes/paths'

import type { SleepLevel } from '../sleep-graph'
import { SleepGraph } from '../sleep-graph'

type Props = {
  sleep: GarminSleepSummary | null
  className?: string
}

/** 時刻は JST の壁時計（tz なし ISO）で来る */
const wallTime = (iso: string | null) => (iso ? iso.slice(11, 16) : '--:--')

const EpisodeGraph = ({ sleep }: { sleep: GarminSleepSummary | null }) => {
  const levels: SleepLevel[] = useMemo(
    () =>
      (sleep?.levels ?? []).map((l) => ({
        startTime: l.start_time,
        endTime: l.end_time,
        level: l.level,
      })),
    [sleep?.levels],
  )
  const stages = sleep?.stages

  return (
    <SleepGraph
      startTime={sleep?.start_time ?? null}
      endTime={sleep?.end_time ?? null}
      levels={levels}
      stages={{
        deep: stages?.deep_seconds ?? 0,
        light: stages?.light_seconds ?? 0,
        rem: stages?.rem_seconds ?? 0,
        awake: stages?.awake_seconds ?? 0,
      }}
    />
  )
}

export const SleepCard = ({ sleep, className }: Props) => {
  const totalSeconds = sleep?.total_seconds ?? 0
  const episodes = sleep?.episodes ?? []
  const reportPath = sleep ? `${PATHS.SLEEP.HOME}?date=${sleep.date}` : PATHS.SLEEP.HOME

  return (
    <div className={cn('rounded-xl border bg-card p-4 shadow-sm', className)}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Moon className="size-5 text-indigo-500" />
          <span className="text-sm font-medium">睡眠</span>
        </div>
        <div className="flex items-center gap-3">
          {sleep?.score && (
            <div className="flex items-center gap-1">
              <span className="text-xs text-muted-foreground">スコア</span>
              <span className="text-sm font-bold text-indigo-600">{sleep.score}</span>
            </div>
          )}
          <Link to={reportPath} className="flex items-center text-xs text-muted-foreground hover:text-foreground">
            レポート
            <ChevronRight className="size-3.5" />
          </Link>
        </div>
      </div>

      {/* 睡眠時間 */}
      <div className="mb-4 flex items-baseline gap-2">
        <div className="text-3xl font-bold tabular-nums">{sleep ? formatSecondsJa(totalSeconds) : '--'}</div>
        {episodes.length > 1 && <span className="text-xs text-muted-foreground">{episodes.length}回の合計</span>}
      </div>

      {/* 睡眠グラフ（タイムライン + 凡例）。分けて寝た日は睡眠ごとに並べる */}
      {episodes.length > 1 ? (
        <div className="space-y-4">
          {episodes.map((episode) => (
            <div key={episode.start_time}>
              <p className="mb-1 text-xs text-muted-foreground tabular-nums">
                {episode.kind === 'nap' ? '昼寝' : '睡眠'} {wallTime(episode.start_time)}〜{wallTime(episode.end_time)}
                （{formatSecondsJa(episode.total_seconds)}）
              </p>
              <EpisodeGraph sleep={episode} />
            </div>
          ))}
        </div>
      ) : (
        <EpisodeGraph sleep={sleep} />
      )}
    </div>
  )
}
